import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Hero } from "@/components/landing/Hero";
import { WhoIsItFor } from "@/components/landing/WhoIsItFor";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { AgentDNA } from "@/components/landing/AgentDNA";
import { Features } from "@/components/landing/Features";
import { UseCases } from "@/components/landing/UseCases";
import { Integrations } from "@/components/landing/Integrations";
import { CostComparison } from "@/components/landing/CostComparison";
import { Pricing } from "@/components/landing/Pricing";
import { FAQ } from "@/components/landing/FAQ";
import { FaqCtaBanner } from "@/components/landing/FaqCtaBanner";
import { Footer } from "@/components/landing/Footer";
import { Testimonials } from "@/components/landing/Testimonials";
import { Helmet } from "react-helmet-async";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
  buildFAQPageSchema,
  buildWebSiteSchema,
  buildWebPageSchema,
} from "@/lib/seo";
import { faqs as faqItems } from "@/components/landing/FAQ";

const INDEX_TITLE =
  "FabChat | Agentes de IA para WhatsApp e Instagram com CRM integrado";
const INDEX_DESCRIPTION =
  "Plataforma de agentes de IA para WhatsApp e Instagram com CRM integrado: automatize atendimento, qualifique leads e organize follow-up em um só painel.";

const Index = () => {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("dark");
    root.classList.add("light");
    return () => {
      const stored = (localStorage.getItem("theme") || "system") as "light" | "dark" | "system";
      root.classList.remove("light", "dark");
      if (stored === "system") {
        const dark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        root.classList.add(dark ? "dark" : "light");
      } else {
        root.classList.add(stored);
      }
    };
  }, []);

  const baseUrl = getBaseUrl();
  const organizationSchema = buildOrganizationSchema(baseUrl);
  const softwareSchema = buildSoftwareApplicationSchema(baseUrl, INDEX_DESCRIPTION);
  const faqSchema = buildFAQPageSchema(faqItems);
  const webSiteSchema = buildWebSiteSchema(baseUrl);
  const webPageSchema = buildWebPageSchema(baseUrl, { path: "/", title: INDEX_TITLE, description: INDEX_DESCRIPTION });

  return (
    <div className="min-h-screen">
      <SeoHead
        title={INDEX_TITLE}
        description={INDEX_DESCRIPTION}
        canonicalPath="/"
      />
      <Helmet>
        <link rel="alternate" type="text/markdown" href={`${baseUrl}/index.md`} />
        <link rel="help" href={`${baseUrl}/llms.txt`} />
      </Helmet>
      <StructuredData data={[organizationSchema, softwareSchema, faqSchema, webSiteSchema, webPageSchema]} />
      <Header hideThemeSelector />
      <Hero />
      <WhoIsItFor />
      <HowItWorks />
      <AgentDNA />
      <Features />
      <UseCases />
      <Integrations />
      <CostComparison />
      <Testimonials />
      <Pricing />
      <FAQ />
      <FaqCtaBanner />
      <section className="sr-only" aria-label="Sobre a FabChat">
        <div className="container mx-auto px-4 py-12 max-w-3xl text-foreground">
          <h2 className="text-2xl font-semibold tracking-tight mb-4">
            O que é a FabChat
          </h2>
          <p className="mb-4 leading-relaxed">
            A <strong>FabChat</strong> é uma plataforma para criar e operar{" "}
            <strong>agentes de inteligência artificial</strong> no{" "}
            <strong>WhatsApp Business</strong> e no <strong>Instagram</strong>, com{" "}
            <strong>CRM integrado</strong> para sua equipe acompanhar conversas,
            oportunidades e follow-up em um só lugar — sem depender só de planilhas ou
            de várias ferramentas desconectadas.
          </p>
          <p className="mb-4 leading-relaxed">
            Você configura regras, base de conhecimento e integrações; os agentes
            respondem com contexto, qualificam leads e encaminham para humanos quando
            fizer sentido. Assim você reduz tempo de resposta, padroniza o atendimento
            e escala vendas ou suporte sem perder a qualidade da conversa.
          </p>
          <p className="mb-6 leading-relaxed">
            Conheça planos, casos por setor e recursos como chatbot no WhatsApp,
            automação no Instagram e qualificação de leads — e fale com a equipe quando
            quiser uma demonstração alinhada à sua operação.
          </p>
          <nav aria-label="Links internos principais" className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
            <Link to="/planos">Planos</Link>
            <Link to="/contato">Contato</Link>
            <Link to="/llm">Resumo FabChat para IA</Link>
            <a href="/llms.txt">llms.txt</a>
            <Link to="/whatsapp/disparos">Disparo no WhatsApp</Link>
            <Link to="/recuperacao-de-leads">Recuperação de leads</Link>
            <Link to="/catalogo-de-produtos">Catálogo de produtos</Link>
            <Link to="/instagram/agente-ia">Agente de IA no Instagram</Link>
            <Link to="/whatsapp/chatbot">Chatbot WhatsApp</Link>
            <Link to="/qualificacao-de-leads-no-whatsapp">Qualificação de leads no WhatsApp</Link>
            <Link to="/setores/imobiliarias">Imobiliárias</Link>
          </nav>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Index;
